import React from "react";

const BREAKDOWNS = [
  { n: 1, title: "The Speed-to-Lead Play",
    namePattern: "\"60-Second Response System\"",
    buys: "Any business spending $2K+/mo on ads with a human answering inquiries (spas, contractors, dealerships, clinics)",
    pain: "Paid leads answered in hours instead of minutes. The first responder wins the deal.",
    charge: "$3,500 to $6,000 setup + $500 to $1,000/mo",
    delivery: "Chat/SMS agent + qualification questions + calendar booking. Weekend-buildable on existing platforms.",
    converts: "The mystery-shop demo. You inquire, time their reply, and show them their own leak. Proof arrives before the pitch." },
  { n: 2, title: "The Dead Database Play",
    namePattern: "\"Database Revenue Recovery System\"",
    buys: "Real estate teams, mortgage brokers, gyms, coaches: anyone with 1,000+ old leads rotting in a CRM",
    pain: "They already PAID for these leads. Zero follow-up after 30 days. The asset depreciates silently.",
    charge: "$3,000 to $5,000 setup, plus optional per-appointment performance fee",
    delivery: "AI text campaign over the old list, conversational reactivation, live handoff on interest.",
    converts: "Zero new spend required. You're monetizing money they already spent. The offer literally pays for itself out of sunk cost." },
  { n: 3, title: "The After-Hours Play",
    namePattern: "\"Never-Closed Intake System\"",
    buys: "Emergency-adjacent services: HVAC, plumbing, roofing, law firms, urgent-care-ish clinics",
    pain: "The most urgent (highest-value) inquiries happen nights and weekends, and go to voicemail or a message-taking service.",
    charge: "$4,500 to $8,000 setup + $600 to $1,000/mo",
    delivery: "Voice or SMS agent, urgency triage, capture of job/case details, calendar or dispatch handoff.",
    converts: "Anchors against answering services they ALREADY pay ($300 to $800/mo) that only take messages. You're not a new expense; you're an upgrade to an existing line item." },
  { n: 4, title: "The Unclosed Quote Play",
    namePattern: "\"Presented-Not-Scheduled Recovery System\"",
    buys: "Dentists (implants/ortho), cosmetic surgeons, home remodelers, solar: anyone who presents big quotes that die in \"let me think about it\"",
    pain: "Five-figure treatment plans and estimates with zero systematic follow-up after day 3.",
    charge: "$5,000 setup + $750 to $1,000/mo",
    delivery: "Automated multi-touch nurture per open quote, question handling, financing info delivery, re-booking.",
    converts: "One recovered case pays for the year. The math is embarrassing in your favor." },
  { n: 5, title: "The Show-Rate Play",
    namePattern: "\"Booked-to-Showed System\"",
    buys: "High-ticket coaches, consultants, and sales teams running calendars of strategy calls",
    pain: "30 to 50% no-show rates on booked calls. Every no-show is wasted setter cost and closer time.",
    charge: "$2,500 to $4,000 setup + $500/mo",
    delivery: "Confirmation conversations (not just reminders: actual dialogue), objection-softening content drip, same-day re-confirmation, instant no-show rebooking.",
    converts: "Buyers already measure show rate. You're improving a KPI they track, so proof is automatic and renewal is rational." },
  { n: 6, title: "The Renewal Rescue Play",
    namePattern: "\"Silent Churn Recovery System\"",
    buys: "Membership businesses: gyms, med spa memberships, home-service maintenance plans, SaaS-adjacent local services",
    pain: "Memberships lapse by neglect, not decision. Nobody calls. Revenue quietly bleeds out the back door.",
    charge: "$3,000 setup + $400 to $700/mo, or performance per saved member",
    delivery: "Pre-lapse outreach conversations, save offers by decision tree, win-back sequences for the already-lapsed.",
    converts: "Retention revenue is the highest-margin revenue any of these businesses have, and almost none of them work it systematically." },
];

const PATTERN = [
  ["A funded buyer", "already spending money adjacent to the pain"],
  ["A quantifiable leak", "(lost leads, dead quotes, no-shows, lapsed members)"],
  ["An outcome name", "with zero technology words in it"],
  ["A price anchored", "to a salary, a sunk cost, or a tracked KPI"],
  ["Delivery assembled", "from existing platforms by a freelancer, built once"],
];

const Row = ({ label, children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 12, padding: "6px 0" }}>
    <span className="font-mono-plex" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted)" }}>{label}</span>
    <span style={{ fontSize: 15 }}>{children}</span>
  </div>
);

export default function BreakdownLibrary() {
  return (
    <div className="prose-weekend" data-testid="asset-body-breakdowns">
      <p style={{ color: "var(--muted)" }}>Six Complete Offer Blueprints: Name, Buyer, Pain, Price, and Delivery Logic</p>
      <p style={{ fontSize: 15 }}>How to use this: These are model-able blueprints, not case studies of specific companies. Each one follows the same anatomy so you can see the pattern repeat: outcome name, funded buyer, quantified pain, anchored price, cheap delivery. Steal the structure, adapt the market.</p>

      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 18 }}>
        {BREAKDOWNS.map((b) => (
          <div key={b.n} style={{ border: "1px solid var(--line)", borderRadius: 12, padding: 18 }}>
            <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, fontSize: 18, margin: 0 }}>BREAKDOWN {b.n}: {b.title}</h3>
            <div style={{ marginTop: 10 }}>
              <Row label="Offer name pattern">{b.namePattern}</Row>
              <Row label="Who buys">{b.buys}</Row>
              <Row label="The pain">{b.pain}</Row>
              <Row label="What to charge"><strong>{b.charge}</strong></Row>
              <Row label="Delivery logic">{b.delivery}</Row>
              <Row label="Why it converts">{b.converts}</Row>
            </div>
          </div>
        ))}
      </div>

      <h3 style={{ fontFamily: "Archivo, sans-serif", fontWeight: 800, marginTop: 26 }}>The Pattern (Read This Twice)</h3>
      <p style={{ fontSize: 15 }}>Every blueprint above is the same machine wearing different clothes:</p>
      <ol style={{ paddingLeft: 20 }}>
        {PATTERN.map(([b, t], i) => <li key={i} style={{ margin: "6px 0" }}><strong>{b}</strong> {t}</li>)}
      </ol>
      <p style={{ fontSize: 15 }}>When you evaluate any new offer idea, run it against those five lines. If any line is blank, the offer isn't ready.</p>
    </div>
  );
}
